import { brandPalette } from "../../../constants/brandPalette";
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdAccessTime, MdBookmarkBorder, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ExamQuestions from "../../../containers/student/exams/exam-questions";
import ExamAnswersLoading from "../../../containers/student/models/exam-answers-loading";
import {
  closeExamAnswersLoading,
  openExamAnswersLoading,
} from "../../../modules/admin-modules/models/Actions";
import {
  getExamQuestionsRequest,
  getExamsByIdRequest,
  sendAnswersRequest,
} from "../../../modules/students-modules/exams/Actions";
import { getStudentExamQuestionsRequest } from "../../../modules/students-modules/student-exams/Actions";
import { studentInk, studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { isStudentOwnedExamFlow, STUDENT_EXAMS_LIST } from "../../../utils/studentExamPaths";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;
const SERIF_Q = `'Amiri','Scheherazade New','Noto Sans Arabic',serif`;

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  pageBg: studentSurfaces.canvas,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  ink: studentInk,
};

const POST_EXAM_REDIRECT_KEY = "edutopia_post_exam_redirect";

function formatHMS(targetMs) {
  const ms = Math.max(0, targetMs - Date.now());
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function ExamHeaderCountdown({ exam, onExpire }) {
  const [display, setDisplay] = useState("00:00:00");
  const firedRef = useRef(false);

  const targetMs = useMemo(() => {
    if (exam?.timer != 1) return null;
    const mins = Number(exam?.time);
    if (!Number.isFinite(mins) || mins <= 0) return null;
    const raw = typeof window !== "undefined" ? window.localStorage.getItem("startTime") : null;
    let startMs = raw ? new Date(raw).getTime() : Date.now();
    if (!Number.isFinite(startMs)) startMs = Date.now();
    return startMs + mins * 60 * 1000;
  }, [exam?.timer, exam?.time, exam?.id]);

  useEffect(() => {
    if (targetMs == null || !Number.isFinite(targetMs)) return undefined;
    firedRef.current = false;
    const tick = () => {
      setDisplay(formatHMS(targetMs));
      if (Date.now() >= targetMs && !firedRef.current) {
        firedRef.current = true;
        onExpire?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs, onExpire]);

  if (targetMs == null) return null;

  return (
    <HStack
      spacing={2}
      px={4}
      py={2}
      borderWidth="1px"
      borderColor={C.border}
      bg={studentSurfaces.raised}
      borderRadius="xl"
      sx={{
        "html[data-theme='dark'] &": {
          bg: "var(--surface-muted)",
          borderColor: "var(--border-subtle)",
        },
      }}
    >
      <Box as="span" display="inline-flex" color={studentSurfaces.text}>
        <MdAccessTime size={22} />
      </Box>
      <Text
        fontFamily="mono"
        fontWeight="800"
        fontSize="xl"
        color={studentSurfaces.text}
        sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary) !important" } }}
      >
        {display}
      </Text>
    </HStack>
  );
}

const SolveExam = () => {
  const [examQuestions, setExamQuestions] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const pathname = window.location.pathname;
  const isStudentFlow = isStudentOwnedExamFlow(pathname);
  const examId = isStudentFlow
    ? pathname.split("/")[3]
    : pathname.split("/")[2];

  const examQuestionsData = useSelector((state) => state.chapterExams.examQuestions);
  const studentExamQuestions = useSelector((state) => state.studentExams.examQuestions);
  const userData = useSelector((state) => state.user.userData);

  const [answers, setAnswers] = useState([]);
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(0);
  const [studentPdf, setStudentPdf] = useState(null);
  const pdfInputRef = useRef(null);
  const exam = useSelector((state) => state.chapterExams.exam);
  const isLoading = useSelector((state) =>
    isStudentFlow ? state.studentExams.isLoading : state.chapterExams.isLoading
  );
  const questionsLoadError = useSelector((state) =>
    isStudentFlow
      ? state.studentExams.examQuestionsLoadError
      : state.chapterExams.examQuestionsLoadError
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const blockedRedirectRef = useRef(false);

  const exitExamPage = useCallback(() => {
    try {
      window.localStorage.removeItem("startTime");
    } catch {
      /* ignore */
    }
    const saved = sessionStorage.getItem(POST_EXAM_REDIRECT_KEY);
    if (saved) {
      sessionStorage.removeItem(POST_EXAM_REDIRECT_KEY);
      window.location.href = saved;
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(isStudentFlow ? STUDENT_EXAMS_LIST : "/exams/sections");
  }, [navigate, isStudentFlow]);

  useEffect(() => {
    blockedRedirectRef.current = false;
    if (examId) {
      dispatch(getExamsByIdRequest(examId));
      if (isStudentFlow) {
        dispatch(getStudentExamQuestionsRequest(examId));
      } else {
        dispatch(getExamQuestionsRequest(examId));
      }
    }
  }, [examId, dispatch, isStudentFlow]);

  useEffect(() => {
    if (isStudentFlow) {
      setExamQuestions(studentExamQuestions);
    } else {
      setExamQuestions(examQuestionsData);
    }
  }, [studentExamQuestions, examQuestionsData, isStudentFlow]);

  useEffect(() => {
    if (!questionsLoadError?.alreadyAnswered || blockedRedirectRef.current) return;
    blockedRedirectRef.current = true;
    exitExamPage();
  }, [questionsLoadError, exitExamPage]);

  const handelNextQuestion = () => {
    if (selectedQuestionIdx < 0) setSelectedQuestionIdx(examQuestions?.length - 1);
    else if (selectedQuestionIdx >= examQuestions?.length - 1) setSelectedQuestionIdx(0);
    else setSelectedQuestionIdx((s) => s + 1);
  };

  const handelPrevQuestion = () => {
    if (selectedQuestionIdx > examQuestions?.length - 1) setSelectedQuestionIdx(0);
    if (selectedQuestionIdx <= 0) setSelectedQuestionIdx(examQuestions?.length - 1);
    else setSelectedQuestionIdx((s) => s - 1);
  };

  const handelSendAnswers = useCallback(async (pdfFile) => {
    dispatch(openExamAnswersLoading());
    const action = () => {
      dispatch(closeExamAnswersLoading());
      window.localStorage.removeItem("startTime");
      const saved = sessionStorage.getItem(POST_EXAM_REDIRECT_KEY);
      sessionStorage.removeItem(POST_EXAM_REDIRECT_KEY);
      if (saved) {
        window.location.href = saved;
        return;
      }
      isStudentFlow
        ? (window.location.href = STUDENT_EXAMS_LIST)
        : (window.location.href = "/exams/sections");
    };

    const formData = new FormData();
    formData.append("exam_id", examId);
    if (pdfFile) {
      formData.append("student_pdf", pdfFile);
    }
    const n = examQuestions?.length || 0;
    for (let index = 0; index < n; index += 1) {
      const data = answers[index];
      if (data?.question_id != null) {
        formData.append(`answers[${index}][question_id]`, data.question_id);
      }
      if (data?.answer_id != null) {
        formData.append(`answers[${index}][answer_id]`, data.answer_id);
      }
    }

    dispatch(sendAnswersRequest(formData, action));
  }, [answers, dispatch, examId, examQuestions, isStudentFlow]);

  const handleCloseSubmitModal = useCallback(() => {
    setStudentPdf(null);
    if (pdfInputRef.current) pdfInputRef.current.value = "";
    onClose();
  }, [onClose]);

  const handleConfirmSubmit = useCallback(() => {
    if (!studentPdf) return;
    handleCloseSubmitModal();
    handelSendAnswers(studentPdf);
  }, [studentPdf, handleCloseSubmitModal, handelSendAnswers]);

  const handleTimerExpire = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const currentQ = examQuestions[selectedQuestionIdx];
  const sectionTitle =
    currentQ?.section_name ||
    currentQ?.section?.name ||
    currentQ?.part_name ||
    (parsed?.name ? String(parsed.name) : "Exam");

  const gridPattern = {
    bg: C.pageBg,
    backgroundImage: `radial-gradient(${brandPalette.stone} 1px, transparent 1px)`,
    backgroundSize: "32px 32px",
  };

  if (isLoading || questionsLoadError?.alreadyAnswered) {
    return (
      <Flex minH="100vh" align="center" justify="center" {...gridPattern} fontFamily={FONT_STACK}>
        <Spinner size="xl" thickness="4px" color={C.primary} />
      </Flex>
    );
  }

  return (
    <>
      <Box
        minH="100vh"
        fontFamily={FONT_STACK}
        color={C.ink}
        {...gridPattern}
        pt={{ base: "88px", md: "96px" }}
        pb="120px"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
      >
        {/* Fixed header */}
        <Flex
          as="header"
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={50}
          px={{ base: 4, md: 8 }}
          py={4}
          bg={C.pageBg}
          borderBottomWidth="1px"
          borderColor={C.border}
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <Heading
            as="h2"
            fontSize={{ base: "sm", md: "lg" }}
            fontWeight="800"
            color={C.primary}
            textTransform="uppercase"
            letterSpacing="0.06em"
            noOfLines={2}
            maxW={{ base: "200px", md: "50%" }}
          >
            {parsed?.name || exam?.name || "Exam"}
          </Heading>
          <HStack spacing={{ base: 3, md: 6 }} flexShrink={0}>
            <ExamHeaderCountdown exam={exam} onExpire={handleTimerExpire} />
            <Button
              bg={C.accent}
              color="white"
              size="sm"
              px={{ base: 4, md: 8 }}
              py={6}
              borderRadius="xl"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.14em"
              fontSize="xs"
              _hover={{ bg: C.primary }}
              onClick={onOpen}
            >
              Submit exam
            </Button>
            <Avatar
              size="sm"
              name={userData?.name || userData?.username || "Student"}
              src={userData?.image || userData?.avatar}
              borderRadius="md"
              borderWidth="1px"
              borderColor={C.border}
            />
          </HStack>
        </Flex>

        <Box maxW="5xl" mx="auto" px={{ base: 4, md: 8 }}>
          <Flex
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            gap={4}
            mb={8}
            direction={{ base: "column", md: "row" }}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="800"
                color={`${C.primary}B3`}
                textTransform="uppercase"
                letterSpacing="0.2em"
              >
                Question {selectedQuestionIdx + 1} of {examQuestions?.length || 0}
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={C.primary}
                mt={2}
                fontFamily={FONT_STACK}
                lineHeight="1.15"
              >
                {sectionTitle}
              </Heading>
            </Box>
            <HStack spacing={1}>
              <IconButton
                aria-label="Previous question"
                icon={<MdChevronLeft size={28} />}
                variant="outline"
                borderColor={C.border}
                bg={studentSurfaces.raised}
                borderRadius="lg"
                onClick={handelPrevQuestion}
              />
              <IconButton
                aria-label="Next question"
                icon={<MdChevronRight size={28} />}
                variant="outline"
                borderColor={C.border}
                bg={studentSurfaces.raised}
                borderRadius="lg"
                onClick={handelNextQuestion}
              />
            </HStack>
          </Flex>

          <Box position="relative" mb={8}>
            <ExamQuestions
              data={currentQ}
              setAnswers={setAnswers}
              answers={answers}
              selectedQuestionIdx={selectedQuestionIdx}
              design="academic"
              questionSerifFont={SERIF_Q}
              examType={exam?.type}
            />
          </Box>

          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Button
              variant="ghost"
              color={C.primary}
              fontWeight="800"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="0.14em"
              leftIcon={<MdBookmarkBorder size={18} />}
              isDisabled
              opacity={0.45}
              _hover={{}}
            >
              Mark for review
            </Button>
            <Button
              bg={C.primary}
              color="white"
              px={{ base: 8, md: 14 }}
              py={6}
              borderRadius="xl"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.12em"
              fontSize="sm"
              _hover={{ bg: C.accent }}
              boxShadow="md"
              onClick={handelNextQuestion}
            >
              Next question
            </Button>
          </Flex>
        </Box>

        {/* Fixed footer — question grid */}
        <Flex
          as="footer"
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={40}
          bg={studentSurfaces.raised}
          borderTopWidth="1px"
          borderColor={C.border}
          px={{ base: 3, md: 8 }}
          py={3}
          align="center"
          justify="space-between"
          flexWrap="wrap"
          gap={3}
          boxShadow="0 -4px 10px rgba(0,0,0,0.05)"
        >
          <HStack
            spacing={1.5}
            overflowX="auto"
            pb={1}
            css={{
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
            flex="1"
            minW={0}
          >
            {examQuestions?.map((_, index) => {
              const answered = answers[index]?.answer_id != null;
              const isCurrent = selectedQuestionIdx === index;
              return (
                <Button
                  key={index}
                  flexShrink={0}
                  minW="36px"
                  h="36px"
                  p={0}
                  fontSize="11px"
                  fontWeight="800"
                  borderRadius="md"
                  variant="unstyled"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={answered ? C.primary : studentSurfaces.raised}
                  color={answered ? "white" : C.primary}
                  borderWidth={isCurrent ? "2px" : "1px"}
                  borderColor={isCurrent ? C.primary : C.border}
                  boxShadow={isCurrent ? "0 0 0 1px rgba(91, 13, 22,0.15)" : "none"}
                  onClick={() => setSelectedQuestionIdx(index)}
                >
                  {String(index + 1).padStart(2, "0")}
                </Button>
              );
            })}
          </HStack>
          <HStack
            spacing={{ base: 4, md: 6 }}
            pl={{ base: 0, md: 8 }}
            borderLeftWidth={{ base: 0, md: "1px" }}
            borderColor={C.border}
            flexShrink={0}
            display={{ base: "none", lg: "flex" }}
          >
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg={C.primary} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Answered
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg={studentSurfaces.raised} borderWidth="2px" borderColor={C.primary} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Current
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg={studentSurfaces.raised} borderWidth="1px" borderColor={C.border} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Unvisited
              </Text>
            </HStack>
          </HStack>
        </Flex>
      </Box>

      <ExamAnswersLoading />

      <Modal isOpen={isOpen} onClose={handleCloseSubmitModal} isCentered size="lg">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="xl"
          fontFamily={FONT_STACK}
          bg={studentSurfaces.raised}
          borderWidth="1px"
          borderColor={C.border}
          color="var(--text-primary)"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.35)"
        >
          <ModalHeader
            color={C.primary}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            Submit exam
          </ModalHeader>
          <ModalCloseButton
            color={C.primary}
            _hover={{ bg: studentSurfaces.muted }}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          />
          <ModalBody>
            <VStack align="stretch" spacing={5}>
              <Text fontSize="md" color={studentSurfaces.text} lineHeight="tall">
                Upload your worked answers as a PDF. An assistant will review and correct your exam
                manually. This submission cannot be undone.
              </Text>
              <Box>
                <FormLabel
                  fontSize="10px"
                  fontWeight="800"
                  color={C.accent}
                  textTransform="uppercase"
                  letterSpacing="0.12em"
                  mb={2}
                >
                  Answer PDF
                </FormLabel>
                <Flex
                  align="center"
                  gap={3}
                  flexWrap="wrap"
                  p={4}
                  borderRadius="xl"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={C.border}
                  bg={studentSurfaces.muted}
                >
                  <Button
                    as="label"
                    cursor="pointer"
                    bg={C.primary}
                    color="white"
                    borderRadius="xl"
                    fontWeight="800"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    fontSize="xs"
                    px={6}
                    py={5}
                    position="relative"
                    overflow="hidden"
                    _hover={{ bg: C.accent }}
                    flexShrink={0}
                  >
                    Choose PDF
                    <Input
                      ref={pdfInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setStudentPdf(null);
                          return;
                        }
                        const isPdf =
                          file.type === "application/pdf" ||
                          file.name.toLowerCase().endsWith(".pdf");
                        if (!isPdf) {
                          setStudentPdf(null);
                          e.target.value = "";
                          return;
                        }
                        setStudentPdf(file);
                      }}
                      accept=".pdf,application/pdf"
                      type="file"
                      position="absolute"
                      inset={0}
                      opacity={0}
                      cursor="pointer"
                      w="100%"
                      h="100%"
                    />
                  </Button>
                  <Text fontSize="sm" fontWeight="600" color={studentSurfaces.text} noOfLines={2} flex={1} minW={0}>
                    {studentPdf?.name ? studentPdf.name : "No file selected"}
                  </Text>
                </Flex>
                <Text fontSize="xs" fontWeight="600" color={studentSurfaces.text} mt={2} opacity={0.75}>
                  PDF only
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={handleCloseSubmitModal}
              color={C.primary}
              fontWeight="700"
              sx={{
                "html[data-theme='dark'] &": { color: "var(--text-primary)" },
                "html[data-theme='dark'] &:hover": { bg: "var(--surface-muted)" },
              }}
            >
              Cancel
            </Button>
            <Button
              bg={C.primary}
              color="white"
              borderRadius="lg"
              onClick={handleConfirmSubmit}
              isDisabled={!studentPdf}
              _hover={{ bg: C.accent }}
              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            >
              Submit exam
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SolveExam;
